<template>
    <div v-if="orderData">
        <PageSection>
            <div class="d-flex justify-content-between align-items-center">
                <div>
                    <h1 class="h4 mb-4">Change Account</h1>
                </div>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <div class="text-muted mb-1">Order ID</div>
                    <div class="h4 mb-2">#{{ orderData.id }}</div>
                    <div class="text-muted mb-0">Patient Reference</div>
                    <h1 class="h4 mb-0">{{ orderData.patient_name }}</h1>
                </div>
                <div>
                    <div><strong>Practice: </strong>{{orderData.practice.company_name}}</div>
                    <div><strong>Dentist: </strong>{{orderData.dentist.first_name}} {{orderData.dentist.surname}}</div>
                </div>
            </div>
        </PageSection>

        <form @submit.prevent="submitAccountChange">
            <div v-if="practices.length > 1" class="col-md-6 mb-4">
                <DropdownInput
                    label="Practice"
                    v-model="form.practice_id"
                    :v="v$.form.practice_id"
                    :options="practices"
                />
            </div>

            <div class="col-md-6 mb-4">
                <DropdownInput
                    label="Account"
                    v-model="form.account_id"
                    :v="v$.form.account_id"
                    :options="accounts"
                />
            </div>

            <div class="buttons">
                <button class="btn btn-primary me-1" type="submit">Submit</button>
                <router-link class="btn btn-light" :to="{ name: 'Order' }">Cancel</router-link>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
    .uploads {
        display:flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        margin-bottom:30px;
        width:500px;
        float:right;
        margin-top:-50px;

        .scan, .photo {
            width: 49%;
        }

        .uploads-submit {
            margin-top:20px; 
        }
    }

    .product-table
    {
        border-collapse: collapse;
        width: 100%;
        line-height: 1em;

        &.product-table-collapsed
        {
            width: auto;
        }

        td,
        th
        {
            vertical-align: top;
            padding-top: 0.3em;
            padding-bottom: 0.3em;
        }

        .column-number
        {
            width: 15%;
            white-space: nowrap;
            text-align: right;
        }
    }

    .progress-events
    {
        color: $swift-mid-grey;
    }
</style>

<script>
    import api from "@/services/api";
    import PageSection from '@/components/PageSection.vue';
    import DropdownInput from '@/components/DropdownInput.vue';
    import useVuelidate from '@vuelidate/core';
    import { required } from '@vuelidate/validators';
    import { helpers } from '@vuelidate/validators';

    export default {
        setup () {
            return {
                v$: useVuelidate()
            }
        },
        props: {
            id: {
                required: true
            },
        },
        components: {            
            PageSection,
            DropdownInput,
        },
        data() {
            return {
                orderData: null,
                practices: [],
                accounts: [],
                isLoading: true,
                form: {
                    practice_id: '',
                    account_id: '',
                },
            };
        },
        mounted () {
            this.fetchOrderData();
            this.fetchPractices();
        },
        computed: {
        },
        watch: {
            'form.practice_id'(newVal) {
                if (this.isLoading) {
                    return;
                }

                this.form.account_id = '';

                if (!newVal) {
                    this.accounts = [];
                    return;
                }

                this.fetchAccounts();
            }
        },
        methods: {
            fetchOrderData: function() {                
                api
                .get('/api/v1/orders/' + this.id)
                .then((response) => {
                    this.orderData = response.data.data;
                    this.form.practice_id = this.orderData.practice?.id;
                    this.fetchAccounts().then(() => {
                        this.form.account_id = this.orderData.account?.id;
                    });

                    this.isLoading = false;
                })
                .catch(() => {
                    console.log('Catch order error');
                });
            },

            fetchPractices: function() {
                api
                .get('/api/v1/practices')
                .then(response => {
                    this.practices = response.data.data.map(practice => ({
                        id: practice.id,
                        label: practice.company_name,
                    }));
                })
                .catch(() => {
                    console.log('Catch practice error');
                });
            },

            fetchAccounts: function() {
                const practiceId = this.form?.practice_id || this.orderData?.practice?.id;

                if (!practiceId) {
                    console.warn('No practiceId available for fetchAccounts');
                    return Promise.resolve();
                }

                return api
                    .get('/api/v1/accounts', {
                        params: {
                            practice_id: practiceId,
                            bookingOnCheck: true,
                        }
                    })
                    .then((response) => {
                        this.accounts = response.data.data.map((account) => ({
                            id: account.id,
                            label: `${account.dentist.first_name} ${account.dentist.surname} (${account.code})`,
                        }));

                        return response;
                    })
                    .catch((error) => {
                        console.log('Catch accounts error', error);
                        throw error;
                    });
            },
            submitAccountChange: function() {
                this.v$.$validate().then((isValid) => {
                    if (!isValid) {
                        return;
                    }

                    const currentPracticeId = this.$store.state.currentPracticeId;
                    const selectedPracticeId = this.form.practice_id;

                    console.log('Submitting account change', {
                        currentPracticeId,
                        selectedPracticeId,
                    });

                    api
                    .post('/api/v1/orders/' + this.id + '/update-account', {
                        account_id: this.form.account_id,
                    })
                    .then(() => {
                        if (currentPracticeId == selectedPracticeId) {
                            this.$router.push({ name: 'Order', params: { id: this.id } });
                        } else {
                            this.$router.push({ name: 'Orders' });
                        }
                    })
                    .catch(() => {
                        console.log('Catch order update account error');
                    });
                });
            },
        },
        
        validations() {
            return {
                form: {
                    practice_id: {
                        required: helpers.withMessage('Please select a practice', required),
                    },
                    account_id: {
                        required: helpers.withMessage('Please select an account', required),
                    },
                }
            };
        },
    }
</script>